import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { UserService } from '../services/api/userService';
import { supabase } from '../services/supabase/client';
import { User } from '../types';
import { isEmailVerifiedViaPublicRpc } from '../utils/emailVerificationRpc';
import {
  clearAnalyticsUserIdentity,
  MixpanelAnalytics,
  syncAnalyticsUserIdentity,
} from '../analytics';
import { Capacitor } from '@capacitor/core';
import { signInWithNativeGoogle } from '../services/auth/nativeGoogleSignIn';
import { saveRememberedEmail } from '../utils/rememberedEmail';

/** 客戶註冊結果（需驗證信時無 session，不應寫入 currentUser） */
export interface CustomerRegisterResult {
  message: string;
  needsEmailVerification: boolean;
}

interface AuthContextType {
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (name: string, email: string, password: string, phone?: string) => Promise<CustomerRegisterResult>;
  verifyEmail: (email: string) => Promise<{ success: boolean; error?: string; user?: User }>;
  resendVerificationCode: (email: string) => Promise<string>;
  logout: () => void;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  forgotPassword: (email: string) => Promise<string>;
  resetPassword: (newPassword: string) => Promise<string>;
  signInWithOAuth: (
    provider: 'google' | 'line'
  ) => Promise<{ completedInApp: boolean }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_INIT_TIMEOUT_MS = Capacitor.isNativePlatform() ? 12000 : 8000;

async function loadAppUserFromSession(session: Session | null): Promise<User | null> {
  if (!session?.user) return null;
  try {
    return await UserService.getCurrentUser();
  } catch (error) {
    console.error('[AuthContext] getCurrentUser 失敗:', error);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const initialSessionDoneRef = useRef(false);

  const finishBootstrap = useCallback(() => {
    if (!initialSessionDoneRef.current) {
      initialSessionDoneRef.current = true;
      setIsLoading(false);
    }
  }, []);

  const syncUserWithSession = useCallback(
    async (session: Session | null, event: string) => {
      if (event === 'SIGNED_OUT') {
        clearAnalyticsUserIdentity();
        setCurrentUser(null);
        return;
      }

      let user = await loadAppUserFromSession(session);
      if (!user && session) {
        try {
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError && refreshData.session) {
            user = await loadAppUserFromSession(refreshData.session);
          }
        } catch (refreshErr) {
          console.warn('[AuthContext] refreshSession 失敗:', refreshErr);
        }
      }
      if (user) {
        syncAnalyticsUserIdentity(user);
        setCurrentUser(user);
        if (user.email) {
          void saveRememberedEmail(user.email);
        }
      } else if (session) {
        console.warn('[AuthContext] 有 session 但無法載入 users 資料，event=', event);
        setCurrentUser(null);
      } else {
        setCurrentUser(null);
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;

    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        finishBootstrap();
      }
    }, AUTH_INIT_TIMEOUT_MS);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;

      const handle = async () => {
        if (event === 'INITIAL_SESSION') {
          await syncUserWithSession(session, event);
          finishBootstrap();
          return;
        }

        if (event === 'SIGNED_IN') {
          await syncUserWithSession(session, event);
          return;
        }

        if (event === 'SIGNED_OUT') {
          await syncUserWithSession(null, event);
          return;
        }

        if (event === 'TOKEN_REFRESHED' && session) {
          void (async () => {
            const user = await loadAppUserFromSession(session);
            if (user) {
              setCurrentUser((prev) => prev ?? user);
            }
          })();
        }
      };

      void handle();
    });

    void supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (cancelled || initialSessionDoneRef.current) return;
      if (error) {
        console.error('[AuthContext] getSession 失敗:', error);
        finishBootstrap();
        return;
      }
      void syncUserWithSession(session, 'BOOTSTRAP_GET_SESSION').finally(() => {
        if (!cancelled && !initialSessionDoneRef.current) {
          finishBootstrap();
        }
      });
    });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [finishBootstrap, syncUserWithSession]);

  const login = async (email: string, password: string) => {
    const user = await UserService.login(email, password);

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      console.error('[AuthContext] 登入後無有效 session:', sessionError);
      await supabase.auth.signOut();
      throw new Error('登入未能建立工作階段，請重試');
    }

    syncAnalyticsUserIdentity(user);
    MixpanelAnalytics.userLoggedIn({
      login_method: 'email',
      is_returning_user: true,
      user_type: user.userType,
    });
    setCurrentUser(user);
    await saveRememberedEmail(email);
    return user;
  };

  const register = async (name: string, email: string, password: string, phone: string = '') => {
    const registrationData = {
      userType: 'customer' as const,
      basicInfo: { email, password, name, phone },
      typeSpecificInfo: { preferredContactMethod: 'email' },
    };

    const { user, error, needsEmailVerification } = await UserService.register(registrationData);
    if (error) throw new Error(error);
    if (user) {
      if (!needsEmailVerification) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('註冊未能建立工作階段，請登入');
        }
        syncAnalyticsUserIdentity(user);
        MixpanelAnalytics.userSignedUp({
          signup_method: 'email',
          user_type: user.userType,
        });
        setCurrentUser(user);
      } else {
        MixpanelAnalytics.userSignedUp({
          signup_method: 'email',
          user_type: user.userType,
        });
      }
      return {
        message: needsEmailVerification
          ? '註冊成功！請至信箱點擊驗證連結後再登入。'
          : '註冊成功！',
        needsEmailVerification: !!needsEmailVerification,
      };
    }
    throw new Error('註冊失敗，請稍後再試');
  };

  const verifyEmail = async (email: string) => {
    const trimmed = email.trim();

    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.email_confirmed_at) {
      const userData = await UserService.getCurrentUser();
      if (userData) {
        setCurrentUser(userData);
        return { success: true, user: userData };
      }
    }

    const { data: { user }, error: getUserError } = await supabase.auth.getUser();
    if (!getUserError && user?.email_confirmed_at) {
      const userData = await UserService.getCurrentUser();
      if (userData) {
        setCurrentUser(userData);
        return { success: true, user: userData };
      }
    }

    if (await isEmailVerifiedViaPublicRpc(trimmed)) {
      return { success: true };
    }

    return { success: false, error: '信箱尚未驗證，請檢查信箱並點擊驗證連結' };
  };

  const resendVerificationCode = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim(),
    });

    if (error) {
      const msg = error.message || '';
      if (/rate|limit|security|too many|頻繁/i.test(msg)) {
        throw new Error('寄送過於頻繁，請約 1 分鐘後再試');
      }
      throw new Error(msg || '重新發送驗證信失敗');
    }
    return '驗證信已重新發送，請檢查信箱並點擊驗證連結。';
  };

  const logout = async () => {
    await UserService.logout();
    clearAnalyticsUserIdentity();
    setCurrentUser(null);
  };

  const updateProfile = async (userData: Partial<User>) => {
    if (!currentUser) throw new Error('用戶未登入');
    const { success, error } = await UserService.updateProfile(currentUser.id, userData);
    if (!success) throw new Error(error || '更新失敗');
    setCurrentUser((prev) => (prev ? { ...prev, ...userData } : null));
  };

  const forgotPassword = async (email: string) => {
    const { success, error } = await UserService.forgotPassword(email);
    if (!success) throw new Error(error || '發送密碼重置郵件失敗');
    return '密碼重置郵件已發送，請檢查您的郵箱並按照指示重置密碼。';
  };

  const resetPassword = async (newPassword: string) => {
    const { success, error } = await UserService.resetPassword(newPassword);
    if (!success) throw new Error(error || '密碼重置失敗');
    return '密碼重置成功，請使用新密碼登入。';
  };

  const signInWithOAuth = async (
    provider: 'google' | 'line'
  ): Promise<{ completedInApp: boolean }> => {
    try {
      if (provider === 'google' && Capacitor.isNativePlatform()) {
        await signInWithNativeGoogle();
        return { completedInApp: true };
      }

      const supabaseOAuthProvider = provider === 'line' ? 'custom:line' : provider;
      const redirectBase =
        (import.meta.env.VITE_APP_URL as string | undefined)?.trim().replace(/\/$/, '') ||
        window.location.origin;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: supabaseOAuthProvider as 'google',
        options: {
          redirectTo: `${redirectBase}/auth?provider=${provider}`,
          ...(provider === 'google'
            ? {
                queryParams: {
                  access_type: 'offline',
                  prompt: 'select_account',
                },
              }
            : {}),
        },
      });

      if (error) {
        let errorMessage = error.message;
        if (error.message.includes('provider is not enabled')) {
          errorMessage = `provider is not enabled: ${provider} 提供商尚未在 Supabase Dashboard 中啟用`;
        } else if (error.message.includes('could not be found')) {
          errorMessage = `Provider ${provider} could not be found: 提供商未找到，請檢查 Supabase 配置（LINE 需建立 custom:line）`;
        }
        throw new Error(errorMessage);
      }

      return { completedInApp: false };
    } catch (error) {
      console.error('OAuth 登录错误:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        isAuthenticated: !!currentUser,
        isLoading,
        login,
        register,
        verifyEmail,
        resendVerificationCode,
        logout,
        updateProfile,
        forgotPassword,
        resetPassword,
        signInWithOAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
